import { DEFAULT_SEGMENT_ID } from '@candulabs/core';
import { connectWithHammock, ENDPOINTS } from 'hammock';
import React, { useMemo } from 'react';

import { FilterTypes } from '@candu-ai/analytics/search';
import { QueryId, QueryParamUrlObject } from '@candu-ai/analytics/types';
import { CollapsableTable } from '@candu-ai/analytics/ui';
import { ACTION_COLUMN } from 'canoe/components/Table/types';
import { getLocalisedNumber } from 'commons/utils';
import { TableLink } from 'dashboard/components/TableLinks';
import { segmentPath } from 'dashboard/pages/Segment';

import { TableFilterActions } from './TableFilterActions';

interface TableProps {
  queryId: QueryId;
  queryParams: QueryParamUrlObject;
  segments: any[];
  title: string;
}

const key = 'withEveryoneSegment(segment_id)';

const BySegmentTableImpl = ({ queryId, queryParams, segments, title }: TableProps) => {
  const nameLookup = useMemo(() => Object.fromEntries(segments.map((c: any) => [c.id, c.name])), [
    segments,
  ]);

  const columns = [
    {
      key: 'SegmentId',
      title: 'Segment',
      colSpan: 6,
      render: (_: string, record: any) => {
        const segmentId = record[key];
        if (segmentId === DEFAULT_SEGMENT_ID) {
          return DEFAULT_SEGMENT_ID;
        }
        const name = nameLookup[segmentId] || segmentId;
        return <TableLink text={name} link={segmentPath(segmentId)} />;
      },
    },
    {
      key: 'count',
      title,
      render: (value: number) => getLocalisedNumber(value, 'en-US'),
    },
    {
      key: 'total',
      title: 'Percentage',
      render: (text: string, record: any) =>
        `${((record.count * 100.0) / record.total).toFixed(1)}%`,
    },
    {
      key: ACTION_COLUMN,
      title: 'actions',
      render: (text: string, record: any) => (
        <TableFilterActions
          filterType={FilterTypes.SegmentId}
          value={record['withEveryoneSegment(segment_id)']}
        />
      ),
    },
  ];

  return (
    <CollapsableTable
      queryId={queryId}
      label={`${title} by Segment`}
      queryParams={queryParams}
      columns={columns}
    />
  );
};

const BySegmentTable = connectWithHammock({
  endpoint: ENDPOINTS.segmentsDefinition,
  resourceName: 'segments',
})(BySegmentTableImpl);

interface Props {
  queryParams: QueryParamUrlObject;
}

export const ViewsBySegmentTable = ({ queryParams }: Props) => (
  <BySegmentTable queryId={QueryId.ViewsBySegmentIdTable} queryParams={queryParams} title="Views" />
);

export const InteractionsBySegmentTable = ({ queryParams }: Props) => (
  <BySegmentTable
    queryId={QueryId.InteractionsBySegmentIdTable}
    queryParams={queryParams}
    title="Interactions"
  />
);

export const ChecklistsCompletedBySegmentTable = ({ queryParams }: Props) => (
  <BySegmentTable
    queryId={QueryId.ChecklistsCompletedBySegmentIdTable}
    queryParams={queryParams}
    title="Checklists Completed"
  />
);

export const FormsSubmittedBySegmentTable = ({ queryParams }: Props) => (
  <BySegmentTable
    queryId={QueryId.FormsSubmittedBySegmentIdTable}
    queryParams={queryParams}
    title="Forms Submitted"
  />
);
